import useQuery, { useGetQueryData, useMutateBody } from "./useQuery";
import React from "react";
import call, { type Output } from "./call";
import { useChannelUpdates } from "./ws/useChannelUpdates";
import type { Project } from "./useProjects";
import { comparatorBy } from "diagnostics-lib/comparator";
const comparator = comparatorBy("createdAt");
export type Source = Output<"GET", "/sources">["body"][number];
const cache = new WeakMap<Source[], Source[]>();
export const useSources = () => {
  const sources = useQuery("/sources").body;
  const getCachedSources = useGetQueryData("/sources");
  const fetchUpdatedItems = React.useCallback(async (since: string) => {
    const { collection, deletedIds } = (
      await call("GET", "/sources/updates", {
        query: { since: new Date(since) },
      })
    ).body;
    return [collection, deletedIds] as const;
  }, []);

  useChannelUpdates(
    { key: "sources" },
    {
      readItems: React.useCallback(() => getCachedSources()?.body, [getCachedSources]),
      fetchUpdatedItems,
      mutateItems: useMutateBody("/sources"),
    },
  );

  return React.useMemo(() => {
    const cached = cache.get(sources);
    if (cached) return cached;
    const result = sources.sort(comparator);
    cache.set(sources, result);
    return result;
  }, [sources]);
};

export const meanInterval = ({ snapshotCount, minSnapshotDate, maxSnapshotDate }: Source) =>
  Math.ceil(
    snapshotCount > 1 && minSnapshotDate && maxSnapshotDate
      ? (new Date(maxSnapshotDate).getTime() - new Date(minSnapshotDate).getTime()) /
          (snapshotCount - 1)
      : 0,
  );

export const useProjectSource = (project: Project | null) => {
  const sources = useSources();
  if (!project) return null;
  const source = sources.find(s => s.id === project.sourceId);
  return source ?? null;
};

export const useSource = (projectSourceId: string) => {
  const sources = useSources();
  return sources.find(s => s.id === projectSourceId) ?? null;
};
